import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API, { crearUsuario, obtenerAreas } from '../../utils/api';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'gerencia',       label: 'Gerencia',       icon: '👔' },
  { value: 'administracion', label: 'Administración', icon: '📋' },
  { value: 'produccion',     label: 'Producción',     icon: '🏭' },
  { value: 'punto_venta',    label: 'Punto de Venta', icon: '🏪' },
  { value: 'empleado',       label: 'Empleado',       icon: '👷' },
];

const rolColor = {
  gerencia:       { bg:'#EDE9FE', color:'#5B21B6' },
  administracion: { bg:'#DBEAFE', color:'#1E40AF' },
  produccion:     { bg:'#D1FAE5', color:'#065F46' },
  punto_venta:    { bg:'#FEF3C7', color:'#92400E' },
  empleado:       { bg:'#F3F4F6', color:'#374151' },
};

const M = {
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
  modal:   { background:'white', borderRadius:16, width:'100%', maxWidth:500, boxShadow:'0 24px 64px rgba(0,0,0,0.3)', overflow:'hidden' },
  header:  { background:'#1a1a2e', color:'white', padding:'18px 24px' },
  footer:  { display:'flex', gap:10, padding:'16px 24px', borderTop:'1px solid #F3F4F6', justifyContent:'flex-end' },
  input:   { width:'100%', padding:'9px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' },
  label:   { fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.05em' },
  btnSec:  { padding:'10px 20px', border:'2px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:14, fontWeight:600, color:'#374151' },
  btnPri:  { padding:'10px 24px', border:'none', borderRadius:8, background:'linear-gradient(135deg,#1a1a2e,#16213e)', color:'white', cursor:'pointer', fontSize:14, fontWeight:700 },
};

function Campo({ label, children }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
      <label style={M.label}>{label}</label>
      {children}
    </div>
  );
}

// ─── MODAL EDITAR USUARIO ─────────────────────────────────
function ModalEditarUsuario({ usuario, areas, onClose, onGuardado }) {
  const [form, setForm] = useState({
    nombre:   usuario.nombre,
    email:    usuario.email,
    rol:      usuario.rol,
    area_id:  usuario.area_id || '',
    activo:   usuario.activo,
    password: '',
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleGuardar = async () => {
    if (!form.nombre || !form.email || !form.rol) { toast.error('Nombre, email y rol son obligatorios'); return; }
    setGuardando(true);
    try {
      await API.put(`/usuarios/${usuario.id}`, form);
      toast.success('Usuario actualizado');
      onGuardado(); onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  return (
    <div style={M.overlay}>
      <div style={M.modal}>
        <div style={M.header}>
          <div style={{ fontSize:13, opacity:0.7 }}>Editar usuario</div>
          <div style={{ fontSize:18, fontWeight:800 }}>👤 {usuario.nombre}</div>
        </div>
        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Campo label="Nombre completo *">
              <input value={form.nombre} onChange={set('nombre')} style={M.input} />
            </Campo>
            <Campo label="Email *">
              <input value={form.email} onChange={set('email')} type="email" style={M.input} />
            </Campo>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Campo label="Rol *">
              <select value={form.rol} onChange={set('rol')} style={M.input}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.icon} {r.label}</option>)}
              </select>
            </Campo>
            <Campo label="Área / Punto de venta">
              <select value={form.area_id} onChange={set('area_id')} style={M.input}>
                <option value="">Sin área</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </Campo>
          </div>
          <Campo label="Nueva contraseña (dejar vacío para no cambiar)">
            <input value={form.password} onChange={set('password')} type="password" placeholder="••••••••" style={M.input} />
          </Campo>
          <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:14, fontWeight:600, color:'#374151' }}>
            <input type="checkbox" checked={form.activo} onChange={set('activo')} style={{ width:16, height:16 }} />
            Usuario activo
          </label>
        </div>
        <div style={M.footer}>
          <button onClick={onClose} style={M.btnSec}>Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando} style={M.btnPri}>
            {guardando ? '⏳ Guardando...' : '✓ Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MÓDULO PRINCIPAL ─────────────────────────────────────
export default function Usuarios() {
  const { puedeVer, usuario: usuarioActual } = useAuth();
  const esGerencia = puedeVer('gerencia');

  const [areas, setAreas]             = useState([]);
  const [usuarios, setUsuarios]       = useState([]);
  const [cargando, setCargando]       = useState(false);
  const [modalEditar, setModalEditar] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm]               = useState({ nombre:'', email:'', password:'', rol:'', area_id:'' });
  const [guardando, setGuardando]     = useState(false);
  const [busqueda, setBusqueda]       = useState('');

  const cargar = () => {
    obtenerAreas().then(r => setAreas(r.data)).catch(() => {});
    if (esGerencia) {
      setCargando(true);
      API.get('/usuarios')
        .then(r => setUsuarios(r.data))
        .catch(() => toast.error('Error cargando usuarios'))
        .finally(() => setCargando(false));
    }
  };

  useEffect(() => { cargar(); }, []);

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleCrear = async () => {
    if (!form.nombre || !form.email || !form.password || !form.rol) {
      toast.error('Completa todos los campos obligatorios'); return;
    }
    setGuardando(true);
    try {
      await crearUsuario(form);
      toast.success(`Usuario ${form.nombre} creado`);
      setForm({ nombre:'', email:'', password:'', rol:'', area_id:'' });
      setMostrarForm(false);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al crear usuario');
    } finally { setGuardando(false); }
  };

  const usuariosFiltrados = usuarios.filter(u =>
    u.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    u.email.toLowerCase().includes(busqueda.toLowerCase())
  );

  const usuariosPorRol = ROLES.map(r => ({
    ...r,
    usuarios: usuariosFiltrados.filter(u => u.rol === r.value),
  })).filter(r => r.usuarios.length > 0);

  return (
    <div style={{ fontFamily:"'Segoe UI',sans-serif", maxWidth:800 }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:'#111827' }}>👤 Usuarios</h1>
          <p style={{ margin:'4px 0 0', color:'#6B7280', fontSize:14 }}>
            {esGerencia ? `${usuarios.length} usuarios registrados` : 'Gestión de accesos'}
          </p>
        </div>
        {(esGerencia || puedeVer('administracion')) && (
          <button onClick={() => setMostrarForm(v => !v)} style={{ ...M.btnPri, padding:'10px 20px' }}>
            {mostrarForm ? '✕ Cancelar' : '+ Nuevo usuario'}
          </button>
        )}
      </div>

      {/* Formulario nuevo usuario */}
      {mostrarForm && (
        <div style={{ background:'white', borderRadius:14, padding:24, boxShadow:'0 1px 4px rgba(0,0,0,0.08)', marginBottom:24 }}>
          <h2 style={{ fontSize:16, fontWeight:700, marginBottom:20, color:'#111827', margin:'0 0 20px' }}>Nuevo Usuario</h2>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Campo label="Nombre completo *">
              <input value={form.nombre} onChange={setF('nombre')} style={M.input} placeholder="Ej: Juan García" />
            </Campo>
            <Campo label="Correo electrónico *">
              <input value={form.email} onChange={setF('email')} type="email" style={M.input} placeholder="usuario@gelatus.co" />
            </Campo>
            <Campo label="Contraseña *">
              <input value={form.password} onChange={setF('password')} type="password" style={M.input} placeholder="Mínimo 8 caracteres" />
            </Campo>
            <Campo label="Rol *">
              <select value={form.rol} onChange={setF('rol')} style={M.input}>
                <option value="">Seleccionar rol...</option>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.icon} {r.label}</option>)}
              </select>
            </Campo>
            <div style={{ gridColumn:'1/-1' }}>
              <Campo label="Área (opcional)">
                <select value={form.area_id} onChange={setF('area_id')} style={M.input}>
                  <option value="">Sin área específica</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </Campo>
            </div>
          </div>
          <button onClick={handleCrear} disabled={guardando} style={{ ...M.btnPri, marginTop:20 }}>
            {guardando ? '⏳ Creando...' : '+ Crear Usuario'}
          </button>
        </div>
      )}

      {/* Lista de usuarios — solo gerencia */}
      {esGerencia && (
        <>
          <input
            value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="🔍 Buscar por nombre o email..."
            style={{ ...M.input, marginBottom:16, maxWidth:340 }}
          />

          {cargando ? (
            <div style={{ padding:40, textAlign:'center', color:'#9CA3AF' }}>Cargando...</div>
          ) : (
            usuariosPorRol.map(grupo => (
              <div key={grupo.value} style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
                  {grupo.icon} {grupo.label} · {grupo.usuarios.length}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {grupo.usuarios.map(u => {
                    const col = rolColor[u.rol] || { bg:'#F3F4F6', color:'#374151' };
                    const esSelf = u.id === usuarioActual?.id;
                    return (
                      <div key={u.id} style={{ background:'white', borderRadius:12, padding:'14px 18px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, opacity: u.activo ? 1 : 0.5 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                          <div style={{ width:38, height:38, borderRadius:'50%', background:'#1a1a2e', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:15, flexShrink:0 }}>
                            {u.nombre.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
                              {u.nombre}
                              {esSelf && <span style={{ fontSize:10, background:'#EEF2FF', color:'#4338CA', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>Tú</span>}
                              {!u.activo && <span style={{ fontSize:10, background:'#FEE2E2', color:'#991B1B', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>Inactivo</span>}
                            </div>
                            <div style={{ fontSize:12, color:'#6B7280' }}>
                              {u.email}
                              {u.area_nombre && <span> · {u.area_nombre}</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, fontWeight:700, background:col.bg, color:col.color }}>
                            {ROLES.find(r => r.value === u.rol)?.label}
                          </span>
                          <button
                            onClick={() => setModalEditar(u)}
                            style={{ padding:'6px 14px', border:'2px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:12, fontWeight:700, color:'#374151' }}
                          >
                            ✏️ Editar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {modalEditar && (
        <ModalEditarUsuario
          usuario={modalEditar}
          areas={areas}
          onClose={() => setModalEditar(null)}
          onGuardado={cargar}
        />
      )}
    </div>
  );
}
